import { LanguagesEnum } from '@/api/enums';
import {
  AdminTaskRule,
  AdminTaskRuleFilterBy,
  AdminTaskRuleOperator,
  AdminTaskRuleProduct,
  AdminTaskTagEnum,
  AdminTaskVisibilityGroupCondition,
  EditableTaskFormState,
} from '@/client/admin-tasks';
import { Tag } from '@/client/tags';
import { LanguageContext } from '@/common/context/LanguageContext';
import { adminTaskSchema } from '@/components/admin-tasks/validations/admin-task';
import {
  FormikCalendar,
  FormikDropdown,
  FormikInput,
  FormikMultiSelect,
  FormikTextarea,
} from '@/components/form';
import { useAdminTaskTags } from '@/hooks/query/';
import { AppChip } from '@/ui/chip';
import { FlexContainer } from '@/ui/styled-ui';
import {
  AdminTaskTagOption,
  getCategoryOptions,
  getFilterByOptions,
  getMatchConditionOptions,
  getOperatorOptions,
  getProductOptions,
  getStatusOptions,
  getTaskLanguageOptions,
  mapAdminTaskTagsToOptions,
  TrashIcon,
} from '@/utils/helpers';
import { Field, Form, Formik, useFormikContext } from 'formik';
import { isEqual } from 'lodash';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { v4 } from 'uuid';

const StyledCard = styled.div`
  background: var(--white-main);
  border-radius: var(--semi-medium-border-radius);
  border: 1px solid var(--gray-opacity);
  box-shadow: var(--box-shadow-default);
  padding: var(--default-padding);
  width: 100%;
  cursor: pointer;
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--default-padding);
`;

const StyledHeaderContent = styled(FlexContainer)`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--xxsmall-padding);
`;

const StyledTitle = styled.h2`
  margin: 0;
  font-size: var(--medium-font-size);
  line-height: var(--default-line-height);
  font-weight: 600;
`;

const StyledDescription = styled.p`
  margin: 0;
  font-size: var(--small-font-size);
  line-height: var(--default-line-height);
  min-width: 0;
  width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledHeaderActions = styled(FlexContainer)`
  width: max-content;
  gap: var(--small-padding);
  position: relative;
  z-index: 2;
  align-items: center;
`;

const StyledIconButton = styled.button`
  background: none;
  border: none;
  padding: var(--xxsmall-padding);
  cursor: pointer;
  color: var(--gray-darker);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--black-main);
  }
`;

const StyledChevronIcon = styled.span<{ $isCollapsed: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
  transform: rotate(${({ $isCollapsed }) => ($isCollapsed ? 0 : 180)}deg);
`;

const StyledFormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: var(--default-padding);
  margin-bottom: var(--default-padding);

  .field {
    grid-column: span 3;
  }

  .field-one-third {
    grid-column: span 2;
  }

  .field-two-thirds {
    grid-column: span 4;
  }

  .field-full {
    grid-column: 1 / -1;
  }
`;

const StyledField = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xxsmall-padding);

  label {
    font-size: var(--small-font-size);
    font-weight: 500;
    color: var(--black-main);
  }
`;

const StyledVisibilitySection = styled.div`
  border: 1px solid var(--gray-opacity);
  border-radius: var(--semi-medium-border-radius);
  padding: var(--default-padding);
  margin-top: var(--default-padding);
`;

const StyledVisibilityHeader = styled(FlexContainer)`
  justify-content: space-between;
  align-items: center;
`;

const StyledVisibilityTitle = styled.div`
  display: flex;
  align-items: center;
  gap: var(--small-padding);
  margin-bottom: var(--xxsmall-padding);

  i {
    color: var(--gray-darker);
  }

  h3 {
    margin: 0;
    font-size: var(--medium-font-size);
    font-weight: 600;
    color: var(--black-main);
  }
`;

const StyledVisibilitySubtitle = styled.p`
  margin: 0;
  font-size: var(--small-font-size);
`;

const StyledMatchCondition = styled(FlexContainer)`
  justify-content: flex-start;
  font-weight: 600;
  gap: var(--small-padding);
  font-size: var(--small-font-size);

  .p-dropdown {
    font-weight: 500;
  }
`;

const StyledMatchConditionDropdown = styled.div`
  min-width: 180px;
`;

const StyledRuleBlock = styled.div`
  background: var(--beige-main);
  border-radius: var(--semi-medium-border-radius);
  padding: var(--medium-padding);
  margin-bottom: var(--default-padding);
`;

const StyledRuleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--small-padding);
`;

const StyledRuleLabel = styled.div`
  font-size: var(--small-font-size);
  font-weight: 600;
`;

const StyledRuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--medium-padding);
`;

const StyledAndSeparator = styled.div`
  text-align: center;
  font-weight: 600;
  font-size: var(--small-font-size);
  text-transform: uppercase;
  margin-bottom: var(--default-padding);
`;

const StyledAddRuleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: var(--small-padding);
  padding: var(--medium-padding);
  margin-top: var(--small-padding);
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  color: var(--gray-darker);
  font-size: var(--small-font-size);
  font-weight: 500;
  cursor: pointer;
  background: transparent;

  i {
    color: var(--black-main);
  }
`;

const StyledTagChip = styled(AppChip)`
  .p-chip {
    background: var(--purple-pale) !important;
    color: var(--purple-text) !important;
  }
`;

const StyledCollapsibleSection = styled.div<{ $isCollapsed: boolean }>`
  overflow: hidden;
  max-height: ${({ $isCollapsed }) => ($isCollapsed ? 0 : 2000)}px;
  opacity: ${({ $isCollapsed }) => ($isCollapsed ? 0 : 1)};
  transition: max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const StyledVisibilityRulesContent = styled.div<{ $isExpanded: boolean }>`
  padding-top: ${({ $isExpanded }) =>
    $isExpanded ? 'var(--default-padding)' : 0};
  overflow: hidden;
  max-height: ${({ $isExpanded }) => ($isExpanded ? 2000 : 0)}px;
  opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
  transition: max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const StyledFormContainer = styled(FlexContainer)`
  flex-direction: column;
  border-top: 1px solid var(--gray-opacity);
  padding-top: var(--default-padding);
  margin-top: var(--default-padding);
  cursor: default;
`;

const FormObserver: React.FC<{
  onChange: (values: EditableTaskFormState) => void;
}> = ({ onChange }) => {
  const { values } = useFormikContext<EditableTaskFormState>();
  const prevRef = useRef(values);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!isEqual(prevRef.current, values)) {
      prevRef.current = values;
      onChange(values);
    }
  }, [values, onChange]);

  return null;
};

export type EditableTaskCardProps = {
  initialValues: EditableTaskFormState;
  onValuesChange: (values: EditableTaskFormState) => void;
  onDelete?: () => void;
};

export const EditableTaskCard: React.FC<EditableTaskCardProps> = ({
  initialValues,
  onValuesChange,
  onDelete,
}) => {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const { adminTaskTags, isLoading: isLoadingTags } = useAdminTaskTags();

  const adminTaskTagOptions: AdminTaskTagOption[] = useMemo(
    () =>
      !isLoadingTags
        ? mapAdminTaskTagsToOptions(adminTaskTags, language as LanguagesEnum, t)
        : [],
    [adminTaskTags, isLoadingTags, language, t],
  );
  const [isCollapsed, setIsCollapsed] = useState(true);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={adminTaskSchema(t)}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onSubmit={() => {}}
    >
      {({ values, setFieldValue }) => {
        const handleLanguageChange = (e: DropdownChangeEvent) => {
          const newLang = e.value as string;
          if (newLang === values.taskLanguage) return;

          const updatedMap = {
            ...values.translationsMap,
            [values.taskLanguage]: {
              title: values.title,
              description: values.description,
              buttonName: values.buttonName,
            },
          };

          const newTranslation = updatedMap[newLang] ?? {
            title: '',
            description: '',
            buttonName: '',
          };

          setFieldValue('translationsMap', updatedMap);
          setFieldValue('taskLanguage', newLang);
          setFieldValue('title', newTranslation.title);
          setFieldValue('description', newTranslation.description);
          setFieldValue('buttonName', newTranslation.buttonName);
        };

        return (
          <StyledCard onClick={() => setIsCollapsed((prev) => !prev)}>
            <FormObserver onChange={onValuesChange} />
            <StyledHeader>
              <StyledHeaderContent>
                <FlexContainer align="center" justify="flex-start" gap={8}>
                  <StyledTitle>{values.title}</StyledTitle>
                  {values.tags.map((tag: Tag, index: number) => {
                    const tagOption = adminTaskTagOptions.find(
                      (opt) => opt.id === tag.id,
                    );
                    if (!tagOption) return null;
                    return (
                      <StyledTagChip
                        key={index}
                        size="s"
                        label={tagOption.label}
                        type={
                          tag.key === AdminTaskTagEnum.REQUIRED_GO_LIVE
                            ? 'purple'
                            : 'purple-outline'
                        }
                      />
                    );
                  })}
                </FlexContainer>
                <StyledDescription title={values.description}>
                  {values.description}
                </StyledDescription>
              </StyledHeaderContent>
              <StyledHeaderActions>
                <StyledIconButton
                  type="button"
                  aria-label={
                    isCollapsed ? t('generic.expand') : t('generic.collapse')
                  }
                >
                  <StyledChevronIcon $isCollapsed={isCollapsed}>
                    <i className="pi pi-chevron-down" />
                  </StyledChevronIcon>
                </StyledIconButton>
                <StyledIconButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.();
                  }}
                  aria-label={t('generic.delete')}
                >
                  <TrashIcon />
                </StyledIconButton>
              </StyledHeaderActions>
            </StyledHeader>

            <StyledCollapsibleSection $isCollapsed={isCollapsed}>
              <StyledFormContainer onClick={(e) => e.stopPropagation()}>
                <Form className="w-full">
                  <StyledFormGrid>
                    <StyledField className="field-one-third">
                      <Field
                        name="taskLanguage"
                        label={t('admin.tasks.form.taskLanguage')}
                        component={FormikDropdown}
                        options={getTaskLanguageOptions()}
                        optionLabel="label"
                        optionValue="value"
                        onChange={handleLanguageChange}
                        required
                      />
                    </StyledField>
                    <StyledField className="field-two-thirds">
                      <Field
                        name="title"
                        label={t('admin.tasks.form.title')}
                        component={FormikInput}
                        required
                      />
                    </StyledField>
                    <StyledField className="field field-full">
                      <Field
                        name="description"
                        label={t('admin.tasks.form.description')}
                        component={FormikTextarea}
                        rows={3}
                        required
                      />
                    </StyledField>
                    <StyledField className="field">
                      <Field
                        name="buttonName"
                        label={t('admin.tasks.form.buttonLabel')}
                        component={FormikInput}
                        required
                      />
                    </StyledField>
                    <StyledField className="field">
                      <Field
                        name="buttonUrl"
                        label={t('admin.tasks.form.buttonLink')}
                        component={FormikInput}
                        required
                      />
                    </StyledField>
                    <StyledField className="field">
                      <label>{t('admin.tasks.form.tag')}</label>
                      <Dropdown
                        value={values.tags?.[0]?.id ?? AdminTaskTagEnum.NONE}
                        options={adminTaskTagOptions}
                        optionLabel="label"
                        optionValue="value"
                        dataKey="id"
                        onChange={(e: DropdownChangeEvent) => {
                          const selectedId = e.value as string | null;
                          const selectedOption = adminTaskTagOptions.find(
                            (option) => option.id === selectedId,
                          );
                          const sourceTag = adminTaskTags?.find(
                            (t) => t.id === selectedId,
                          );

                          const tag: Tag | null =
                            selectedId && selectedOption
                              ? {
                                  id: selectedId,
                                  key: sourceTag?.key ?? null,
                                  translations: [
                                    {
                                      language: language as LanguagesEnum,
                                      name: selectedOption.label,
                                    },
                                  ],
                                }
                              : null;

                          setFieldValue('tags', tag ? [tag] : []);
                        }}
                        className="w-full"
                        placeholder={t('generic.select')}
                        loading={isLoadingTags}
                        disabled={isLoadingTags}
                      />
                    </StyledField>
                    <StyledField className="field">
                      <Field
                        name="isForFreeTrial"
                        label={t('admin.tasks.form.category')}
                        component={FormikDropdown}
                        options={getCategoryOptions(t)}
                        optionLabel="label"
                        optionValue="value"
                        required
                      />
                    </StyledField>
                    <StyledField className="field field-full">
                      <Field
                        name="featureFlagKey"
                        label={t('admin.tasks.form.featureFlagKey')}
                        component={FormikInput}
                      />
                    </StyledField>
                  </StyledFormGrid>

                  <StyledVisibilitySection>
                    <StyledVisibilityHeader>
                      <div>
                        <StyledVisibilityTitle>
                          <i className="pi pi-eye" />
                          <h3>{t('admin.tasks.form.visibilityRules')}</h3>
                        </StyledVisibilityTitle>
                        <StyledVisibilitySubtitle>
                          {t('admin.tasks.form.visibilityRulesSubtitle')}
                        </StyledVisibilitySubtitle>
                      </div>
                      <InputSwitch
                        checked={values.visibilityRulesEnabled}
                        onChange={(e) =>
                          setFieldValue(
                            'visibilityRulesEnabled',
                            e.value ?? false,
                          )
                        }
                        className="v2"
                      />
                    </StyledVisibilityHeader>

                    <StyledVisibilityRulesContent
                      $isExpanded={values.visibilityRulesEnabled}
                    >
                      <StyledRuleBlock>
                        <StyledMatchCondition>
                          <span>{t('admin.tasks.form.match')}</span>
                          <StyledMatchConditionDropdown>
                            <Field
                              name="matchCondition"
                              component={FormikDropdown}
                              options={getMatchConditionOptions(t)}
                              optionLabel="label"
                              optionValue="value"
                              className="w-full white"
                            />
                          </StyledMatchConditionDropdown>
                          <span>{t('admin.tasks.form.filtersInGroup')}</span>
                        </StyledMatchCondition>
                      </StyledRuleBlock>

                      {values.visibilityRules?.map(
                        (rule: AdminTaskRule, index: number) => (
                          <React.Fragment key={rule.id}>
                            <StyledRuleBlock>
                              <StyledRuleHeader>
                                <StyledRuleLabel>
                                  {t('admin.tasks.form.rule')} {index + 1}
                                </StyledRuleLabel>
                                {(values.visibilityRules?.length ?? 0) > 1 && (
                                  <StyledIconButton
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFieldValue(
                                        'visibilityRules',
                                        values.visibilityRules?.filter(
                                          (_, i) => i !== index,
                                        ),
                                      );
                                    }}
                                    aria-label={t('generic.delete')}
                                  >
                                    <i className="pi pi-times" />
                                  </StyledIconButton>
                                )}
                              </StyledRuleHeader>
                              <StyledRuleGrid>
                                <div>
                                  <Field
                                    name={`visibilityRules[${index}].product`}
                                    label={t('admin.tasks.form.product')}
                                    component={FormikMultiSelect}
                                    options={getProductOptions(
                                      t,
                                      rule.filterBy,
                                    )}
                                    optionLabel="label"
                                    optionValue="value"
                                    className="w-full white"
                                    placeholder={t('generic.select')}
                                    display="chip"
                                    disabled={
                                      rule.filterBy ===
                                        AdminTaskRuleFilterBy.ACCOUNT_CREATION_DATE &&
                                      rule.product.length === 1 &&
                                      rule.product[0] ===
                                        AdminTaskRuleProduct.ACCOUNT
                                    }
                                  />
                                </div>
                                <div>
                                  <Field
                                    name={`visibilityRules[${index}].filterBy`}
                                    label={t('admin.tasks.form.filterBy')}
                                    component={FormikDropdown}
                                    options={getFilterByOptions(t)}
                                    optionLabel="label"
                                    optionValue="value"
                                    className="w-full white"
                                    onChange={(e: DropdownChangeEvent) => {
                                      const newFilterBy = e.value;
                                      const isDateFilter =
                                        newFilterBy ===
                                          AdminTaskRuleFilterBy.ONBOARDING_DATE ||
                                        newFilterBy ===
                                          AdminTaskRuleFilterBy.ACCOUNT_CREATION_DATE;
                                      setFieldValue(
                                        `visibilityRules[${index}].filterBy`,
                                        newFilterBy,
                                      );
                                      setFieldValue(
                                        `visibilityRules[${index}].operator`,
                                        isDateFilter
                                          ? AdminTaskRuleOperator.AFTER
                                          : AdminTaskRuleOperator.IS,
                                      );
                                      setFieldValue(
                                        `visibilityRules[${index}].status`,
                                        [],
                                      );
                                      setFieldValue(
                                        `visibilityRules[${index}].dateValue`,
                                        null,
                                      );
                                      if (
                                        newFilterBy ===
                                        AdminTaskRuleFilterBy.ACCOUNT_CREATION_DATE
                                      ) {
                                        setFieldValue(
                                          `visibilityRules[${index}].product`,
                                          [AdminTaskRuleProduct.ACCOUNT],
                                        );
                                      } else if (
                                        rule.filterBy ===
                                        AdminTaskRuleFilterBy.ACCOUNT_CREATION_DATE
                                      ) {
                                        setFieldValue(
                                          `visibilityRules[${index}].product`,
                                          [],
                                        );
                                      }
                                    }}
                                  />
                                </div>
                                <div>
                                  <Field
                                    name={`visibilityRules[${index}].operator`}
                                    label={
                                      rule.filterBy ===
                                      AdminTaskRuleFilterBy.ONBOARDING_DATE
                                        ? t('admin.tasks.form.onboarded')
                                        : rule.filterBy ===
                                          AdminTaskRuleFilterBy.ACCOUNT_CREATION_DATE
                                        ? t('admin.tasks.form.created')
                                        : t('admin.tasks.form.status')
                                    }
                                    component={FormikDropdown}
                                    options={getOperatorOptions(
                                      rule.filterBy,
                                      t,
                                    )}
                                    optionLabel="label"
                                    optionValue="value"
                                    className="w-full white"
                                  />
                                </div>
                                <div>
                                  {rule.filterBy ===
                                    AdminTaskRuleFilterBy.ONBOARDING_DATE ||
                                  rule.filterBy ===
                                    AdminTaskRuleFilterBy.ACCOUNT_CREATION_DATE ? (
                                    <Field
                                      name={`visibilityRules[${index}].dateValue`}
                                      label={' '}
                                      component={FormikCalendar}
                                      value={
                                        rule.dateValue
                                          ? new Date(
                                              rule.dateValue.substring(0, 10) +
                                                'T00:00:00',
                                            )
                                          : null
                                      }
                                      onChange={(e: { value: Date | null }) => {
                                        const date = e.value;
                                        setFieldValue(
                                          `visibilityRules[${index}].dateValue`,
                                          date
                                            ? `${date.getFullYear()}-${String(
                                                date.getMonth() + 1,
                                              ).padStart(2, '0')}-${String(
                                                date.getDate(),
                                              ).padStart(2, '0')}`
                                            : null,
                                        );
                                      }}
                                      dateFormat="yy-mm-dd"
                                      placeholder={t(
                                        'admin.tasks.form.selectDate',
                                      )}
                                      className="w-full white-input"
                                      showIcon={false}
                                      minDate={null}
                                      readOnlyInput={false}
                                    />
                                  ) : (
                                    <Field
                                      name={`visibilityRules[${index}].status`}
                                      label={' '}
                                      className="w-full white"
                                      placeholder={t('generic.select')}
                                      optionLabel="label"
                                      optionValue="value"
                                      options={getStatusOptions(t)}
                                      component={FormikMultiSelect}
                                      display="chip"
                                    />
                                  )}
                                </div>
                              </StyledRuleGrid>
                            </StyledRuleBlock>
                            {index <
                              (values.visibilityRules?.length ?? 0) - 1 && (
                              <StyledAndSeparator>
                                {t(
                                  values.matchCondition ===
                                    AdminTaskVisibilityGroupCondition.AND
                                    ? 'admin.tasks.form.andSeparator'
                                    : 'admin.tasks.form.orSeparator',
                                )}
                              </StyledAndSeparator>
                            )}
                          </React.Fragment>
                        ),
                      )}

                      <StyledAddRuleButton
                        type="button"
                        onClick={() => {
                          setFieldValue('visibilityRules', [
                            ...(values.visibilityRules ?? []),
                            {
                              id: v4(),
                              product: [],
                              filterBy: AdminTaskRuleFilterBy.STATUS,
                              operator: AdminTaskRuleOperator.IS,
                              status: [],
                              dateValue: null,
                            } as AdminTaskRule,
                          ]);
                        }}
                      >
                        <i className="pi pi-plus" />
                        {t('admin.tasks.form.addRule')}
                      </StyledAddRuleButton>
                    </StyledVisibilityRulesContent>
                  </StyledVisibilitySection>
                </Form>
              </StyledFormContainer>
            </StyledCollapsibleSection>
          </StyledCard>
        );
      }}
    </Formik>
  );
};
